import PropTypes from "prop-types";
import * as React from "react";
import { useCharacters, useDebounce } from "../../../hooks";
import { bulkProductAction } from "../../../services/bulkProduct";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  Typography,
} from "../../../themeV2";
import { normalizeTagString } from "../../../util/tagString";
import TagSearch from "../../TagSearch";
import css from "./BulkTagsDialog.module.css";

const MIN_CHARACTER_QUERY = 2;

const BulkTagsDialog = React.memo(function BulkTagsDialog({
  isAdmin,
  open,
  onClose,
  selectedIds,
  onActionComplete,
}) {
  const [tags, setTags] = React.useState([]);
  const [characterTag, setCharacterTag] = React.useState(null);
  const [franchiseTag, setFranchiseTag] = React.useState(null);
  const [selectedCharacters, setSelectedCharacters] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  useDebounce(() => setDebouncedQuery(searchQuery), 300, [searchQuery]);

  const { characters: searchResults } = useCharacters({
    q: debouncedQuery.length >= MIN_CHARACTER_QUERY ? debouncedQuery : null,
  });

  const openRef = React.useRef(open);
  const [internalOpen, setInternalOpen] = React.useState(open);

  React.useEffect(() => {
    if (open !== openRef.current) {
      openRef.current = open;
      setInternalOpen(open);
    }
  }, [open]);

  const assignedIds = React.useMemo(
    () => new Set(selectedCharacters.map((c) => c.franchiseCharacterId)),
    [selectedCharacters],
  );

  const filteredResults = React.useMemo(
    () =>
      searchResults.filter((c) => !assignedIds.has(c.franchise_character_id)),
    [searchResults, assignedIds],
  );

  const hasValue =
    tags.length > 0 ||
    characterTag !== null ||
    franchiseTag !== null ||
    selectedCharacters.length > 0;

  const handleClose = React.useCallback(() => {
    setTags([]);
    setCharacterTag(null);
    setFranchiseTag(null);
    setSelectedCharacters([]);
    setSearchQuery("");
    setError(null);
    onClose();
  }, [onClose]);

  /* Normalised on the way in, so the chips show the tag the products will
     actually get and the payload cannot carry two that collide. */
  const handleAddTag = React.useCallback((tag) => {
    const tagString = normalizeTagString(tag);

    setTags((prev) =>
      !tagString || prev.includes(tagString) ? prev : [...prev, tagString],
    );
  }, []);

  const handleRemoveTag = React.useCallback((tagToRemove) => {
    setTags((prev) => prev.filter((t) => t !== tagToRemove));
  }, []);

  const handleAddCharacter = React.useCallback((character) => {
    setSelectedCharacters((prev) => [
      ...prev,
      {
        franchiseCharacterId: character.franchise_character_id,
        franchiseId: character.franchise_id,
        name: character.name,
        franchiseName: character.franchise_name,
      },
    ]);
    setSearchQuery("");
  }, []);

  const handleRemoveCharacter = React.useCallback((franchiseCharacterId) => {
    setSelectedCharacters((prev) =>
      prev.filter((c) => c.franchiseCharacterId !== franchiseCharacterId),
    );
  }, []);

  const handleApply = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const productIds = [...selectedIds];

      if (tags.length > 0) {
        await bulkProductAction({
          productIds,
          action: "addTags",
          tags: tags.map((t) => ({ tagString: t })),
        });
      }

      if (characterTag !== null) {
        await bulkProductAction({
          productIds,
          action: "setCharacterTag",
          characterTagString: normalizeTagString(characterTag),
        });
      }

      if (franchiseTag !== null) {
        await bulkProductAction({
          productIds,
          action: "setFranchiseTag",
          franchiseTagString: normalizeTagString(franchiseTag),
        });
      }

      if (selectedCharacters.length > 0) {
        await bulkProductAction({
          productIds,
          action: "addCharacterAssignments",
          characters: selectedCharacters.map((c) => ({
            franchiseCharacterId: c.franchiseCharacterId,
            franchiseId: c.franchiseId,
          })),
        });
      }

      onActionComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedIds,
    tags,
    characterTag,
    franchiseTag,
    selectedCharacters,
    onActionComplete,
  ]);

  const plural = selectedIds.size !== 1 ? "s" : "";

  return (
    <Dialog.Root
      open={internalOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isLoading) setInternalOpen(false);
      }}
      /* Resetting here rather than on close keeps the content still while the
         dialog animates out. */
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <Dialog.Popup size="md">
        <Dialog.Title>
          Add Tags for {selectedIds.size} Product{plural}
        </Dialog.Title>

        <Dialog.Body>
          <section className={css.section}>
            <Typography variant="title-md" as="h3" className={css.sectionLabel}>
              Product tags
            </Typography>
            <Typography variant="label-lg" color="variant" as="p">
              Adds tags to all selected products
            </Typography>

            <div className={css.control}>
              <TagSearch
                clearOnBlur
                onAddTag={handleAddTag}
                onRemoveTag={() => {}}
              />
            </div>

            {tags.length > 0 && (
              <div className={css.chips}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    variant="input"
                    size="sm"
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                  />
                ))}
              </div>
            )}
          </section>

          {isAdmin && (
            <>
              <section className={css.section}>
                <Typography
                  variant="title-md"
                  as="h3"
                  className={css.sectionLabel}
                >
                  Character tag
                </Typography>
                <Typography variant="label-lg" color="variant" as="p">
                  Replaces existing character tag on all selected products
                </Typography>

                <div className={css.control}>
                  <TagSearch
                    clearOnBlur={false}
                    isCharacter
                    onAddTag={setCharacterTag}
                    onRemoveTag={() => setCharacterTag(null)}
                    tag={characterTag}
                  />
                </div>
              </section>

              <section className={css.section}>
                <Typography
                  variant="title-md"
                  as="h3"
                  className={css.sectionLabel}
                >
                  Franchise tag
                </Typography>
                <Typography variant="label-lg" color="variant" as="p">
                  Replaces existing franchise tag on all selected products
                </Typography>

                <div className={css.control}>
                  <TagSearch
                    clearOnBlur={false}
                    isFranchise
                    onAddTag={setFranchiseTag}
                    onRemoveTag={() => setFranchiseTag(null)}
                    tag={franchiseTag}
                  />
                </div>
              </section>

              <section className={css.section}>
                <Typography
                  variant="title-md"
                  as="h3"
                  className={css.sectionLabel}
                >
                  Assign characters
                </Typography>
                <Typography variant="label-lg" color="variant" as="p">
                  Adds to existing assignments
                </Typography>

                {selectedCharacters.length > 0 && (
                  <div className={css.chips}>
                    {selectedCharacters.map((c) => (
                      <Chip
                        key={c.franchiseCharacterId}
                        variant="input"
                        size="sm"
                        label={`${c.name} (${c.franchiseName})`}
                        onDelete={() =>
                          handleRemoveCharacter(c.franchiseCharacterId)
                        }
                      />
                    ))}
                  </div>
                )}

                <div className={css.control}>
                  <Autocomplete.Root
                    label="Search characters to assign"
                    placeholder="Type a character name"
                    value={null}
                    onValueChange={(character) =>
                      character && handleAddCharacter(character)
                    }
                    inputValue={searchQuery}
                    onInputValueChange={setSearchQuery}
                    /* The results arrive already matched by the server. */
                    autoComplete="none"
                    itemToStringLabel={(c) => c?.name ?? ""}
                  >
                    {filteredResults.map((character) => (
                      <Autocomplete.Item
                        key={character.franchise_character_id}
                        value={character}
                      >
                        <span className={css.result}>
                          <Typography variant="body-lg" as="span">
                            {character.name}
                          </Typography>
                          <Typography
                            variant="label-md"
                            color="variant"
                            as="span"
                          >
                            {character.franchise_name}
                          </Typography>
                        </span>
                      </Autocomplete.Item>
                    ))}

                    <Autocomplete.Empty>
                      {debouncedQuery.length >= MIN_CHARACTER_QUERY
                        ? "No matching characters found."
                        : "Type at least two letters to search."}
                    </Autocomplete.Empty>
                  </Autocomplete.Root>
                </div>
              </section>
            </>
          )}

          {error && (
            <div className={css.error}>
              <Alert severity="error">{error}</Alert>
            </div>
          )}
        </Dialog.Body>

        <Dialog.Actions>
          <Button
            variant="text"
            disabled={isLoading}
            onClick={() => setInternalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            disabled={!hasValue}
            loading={isLoading}
            onClick={handleApply}
          >
            Apply
          </Button>
        </Dialog.Actions>
      </Dialog.Popup>
    </Dialog.Root>
  );
});

BulkTagsDialog.displayName = "BulkTagsDialog";

BulkTagsDialog.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  onActionComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
};

export default BulkTagsDialog;
